import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { isNoiseException } from './exceptionNoise';

let appInsights: ApplicationInsights | null = null;

function normalizedPagePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

export function initAppInsights(connectionString?: string): ApplicationInsights | null {
  if (typeof window === 'undefined') return null;
  if (appInsights) return appInsights;
  if (!connectionString) return null;

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      correlationHeaderExcludedDomains: [
        '*.telemetric.dk',
        '*.bellmetric.net',
        '*.klaviyo.com',
      ],
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
      disableFetchTracking: false,
      disableAjaxTracking: false,
      // Suppress the browser deprecation warning for `unload` event listeners.
      // App Insights uses `unload` by default; this opts in to `pagehide` instead.
      disablePageUnloadEvents: ['unload'],
    },
  });

  appInsights.loadAppInsights();

  // Drop exceptions caused by browser extensions / in-app browsers (not our code).
  appInsights.addTelemetryInitializer((item) => {
    if (item.baseType === 'ExceptionData') {
      const exceptions = (
        item.baseData as { exceptions?: Parameters<typeof isNoiseException>[0] } | undefined
      )?.exceptions;
      if (isNoiseException(exceptions)) return false;
    }
    return true;
  });

  appInsights.addTelemetryInitializer((item) => {
    if (typeof window === 'undefined') return true;
    const pathname = normalizedPagePath(window.location.pathname || '/');
    item.data = {
      ...(item.data ?? {}),
      pagePath: pathname,
    };
    return true;
  });

  appInsights.trackPageView();

  return appInsights;
}

export function trackException(error: Error, severityLevel?: number): void {
  const ai = getAppInsights();
  if (!ai) return;
  const pagePath =
    typeof window !== 'undefined'
      ? normalizedPagePath(window.location.pathname || '/')
      : '/';
  ai.trackException({ exception: error, severityLevel }, { pagePath });
}

export function trackEvent(
  name: string,
  properties?: Record<string, string>,
): void {
  const ai = getAppInsights();
  if (!ai) return;
  ai.trackEvent({ name, properties });
}
